import { closeReconciliationURLs } from 'routes/close/closeURLs';

import { UserAvatar } from 'components/data-display/UserAvatar';

import { useUsersData } from 'hooks/useUsersData';

import { featureFlags } from 'utils/config/featureFlags';
import { getName } from 'utils/users/config';

import { useAuthStore } from 'store/auth.store';

import { GenericNotificationItem } from 'layout/inbox-notifications/GenericNotificationItem';

import {
  type ReconTaskTemplateNotification,
  ReconTaskTemplateCreatedNotification,
  ReconTaskTemplateDeletedNotification,
} from 'types/inbox-notifications/close/ReconciliationNotifications';
import { SegmentAtByDot } from 'types/utils';

const { FF__SHOW_NOTIFICATIONS_DEBUG_MODE: DEBUG_MODE } = featureFlags;

type TemplateLevelKeys = SegmentAtByDot<
  ReconTaskTemplateNotification['type'],
  4
>;

export function TemplateNotificationItem({
  type,
  item,
}: {
  type: string[];
  item: ReconTaskTemplateNotification;
}) {
  const [actionType, ..._restOfType] = type;
  if (!item.template) {
    return <div>Reconciliation task template not found</div>;
  }

  switch (actionType as TemplateLevelKeys) {
    case 'created':
      return (
        <TemplateCreatedNotificationItem
          item={item as ReconTaskTemplateCreatedNotification}
        />
      );
    case 'deleted':
      return (
        <TemplateDeletedNotificationItem
          item={item as ReconTaskTemplateDeletedNotification}
        />
      );
    default:
      return (
        <div>
          Unknown reconciliation task template notification type: {actionType}
        </div>
      );
  }
}

function TemplateCreatedNotificationItem({
  item,
}: {
  item: ReconTaskTemplateCreatedNotification;
}) {
  const { usersMap } = useUsersData();
  const { user } = useAuthStore();
  const actorName = getName(item.actor_id, usersMap);
  const viewerUserId = (DEBUG_MODE && item.debug_user_id) || user?.id;
  if (!viewerUserId) {
    return <div>User not found {DEBUG_MODE && viewerUserId}</div>;
  }

  const isPreparer = item.template.preparers.some(
    (preparer) => preparer.user_id === viewerUserId
  );
  const actorType = isPreparer ? 'preparer' : 'reviewer';

  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.base}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${actorName} assigned you a recurring reconciliation as ${actorType}.`}
      description={<span>&quot;{item.template.core.name}&quot;</span>}
    />
  );
}

function TemplateDeletedNotificationItem({
  item,
}: {
  item: ReconTaskTemplateDeletedNotification;
}) {
  const { usersMap } = useUsersData();
  const actorName = getName(item.actor_id, usersMap);

  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.base}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${actorName} deleted a recurring reconciliation.`}
      description={<span>&quot;{item.template.core.name}&quot;</span>}
    />
  );
}
